import React, { useCallback, useEffect, useState } from 'react';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@material-ui/core/TextField';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { BooleanInput, FormWithRedirect, NumberInput, SaveButton, TextInput, maxValue, minValue, required, useDataProvider, useNotify } from 'react-admin';
import { ColorInput } from 'react-admin-color-input';

import { languageMap, useTranslate } from '../common/language';
import { apiUrl, targets } from '../common/constants';
import { headersAPIObjectAuth } from '../common/utils';
import { ImageViewer } from '../resources/docs';
import { DocsCreateButton } from './DocsCreateButton';
import { DocsDeleteButton } from './DocsPhotoButton';

const LogoConfiguration = ({ title, target }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const [logo, setLogo] = useState(null);

  const loadLogo = useCallback(() => {
    dataProvider.getList('docs', {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'priority', order: 'ASC' },
      filter: { target, items_id: 0 }
    })
      .then(({ data }) => setLogo(data.length ? data[0] : null))
      .catch(() => notify(translate('ra.auth.error_network'), 'error'));
  }, [dataProvider, notify, target, translate]);

  useEffect(() => {
    loadLogo();
  }, [loadLogo]);

  return (
    <Box
      sx={{
        minWidth: 0,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        p: 2,
        backgroundColor: '#fff'
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Box className="ara-tiquets-config-logo">
        <Box textAlign="right" className="photo_box">
          <DocsCreateButton
            target={target}
            items_id={0}
            label=""
            icon={<EditIcon />}
            btnClassname="btnPhotoUser"
            onSuccess={loadLogo}
          />
          <DocsDeleteButton
            target={target}
            items_id={0}
            label=""
            icon={<DeleteIcon />}
            onSuccess={loadLogo}
          />
          {logo && (
            <ImageViewer
              url={logo.url}
              className="ara-tiquets-config-logo-image"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const languageIds = Object.keys(languageMap).map(Number).sort((a, b) => a - b);
const textFields = [
  ['ticket_pdf_legal_text', 'ticket_pdf_legal_text', 4],
  ['web_legal_text', 'web_legal_text', 5],
  ['web_privacy_policy', 'web_privacy_policy', 8],
  ['web_cookie_policy', 'web_cookie_policy', 8],
  ['web_terms_conditions', 'web_terms_conditions', 8],
  ['google_wallet_legal_text', 'google_wallet_legal_text', 6],
  ['pkpass_legal_text', 'pkpass_legal_text', 6]
];

const emptyTranslation = lang => ({
  lang,
  ticket_pdf_legal_text: '',
  web_legal_text: '',
  web_privacy_policy: '',
  web_cookie_policy: '',
  web_terms_conditions: '',
  google_wallet_legal_text: '',
  pkpass_legal_text: ''
});

const normalizeTranslations = translations => languageIds.map(lang => ({
  ...emptyTranslation(lang),
  ...((translations || []).find(item => item.lang === lang) || {}),
  lang
}));

const GeneralConfiguration = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetch(`${apiUrl}/ara-tiquets/configuration`, { headers: headersAPIObjectAuth() })
      .then(response => {
        if (!response.ok) throw new Error('configuration_load_error');
        return response.json();
      })
      .then(data => { if (mounted) setConfiguration(data); })
      .catch(() => notify(translate('ara_tiquets_admin.configuration_load_error'), 'error'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [notify, translate]);

  const save = async values => {
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/ara-tiquets/configuration`, {
        method: 'PUT',
        headers: headersAPIObjectAuth(),
        body: JSON.stringify(values)
      });
      if (!response.ok) throw new Error('configuration_save_error');
      const data = await response.json();
      setConfiguration(data);
      notify(translate('ara_tiquets_admin.configuration_saved'), 'info');
    } catch (error) {
      notify(translate('ara_tiquets_admin.configuration_save_error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : configuration ? (
        <FormWithRedirect
          resource="ara-tiquets-configuration"
          initialValues={configuration}
          save={save}
          render={({ handleSubmitWithRedirect }) => (
            <>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff' }}>
                <Typography variant="h6" sx={{ px: 2.5, pt: 2.5, fontWeight: 600 }}>
                  {translate('ara_tiquets_admin.general_configuration')}
                </Typography>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5 }}>
                    <TextInput
                      source="name"
                      label={translate('ara_tiquets_admin.name')}
                      validate={required()}
                      fullWidth
                    />
                    <TextInput
                      source="public_url"
                      label={translate('ara_tiquets_admin.public_url')}
                      validate={required()}
                      fullWidth
                    />
                    <NumberInput
                      source="max_active_baskets"
                      label={translate('ara_tiquets_admin.max_active_baskets')}
                      validate={[required(), minValue(1)]}
                      options={{ min: 1, step: 1 }}
                      fullWidth
                    />
                    <NumberInput
                      source="max_active_baskets_per_ip"
                      label={translate('ara_tiquets_admin.max_active_baskets_per_ip')}
                      validate={[required(), minValue(1)]}
                      options={{ min: 1, step: 1 }}
                      fullWidth
                    />
                    <NumberInput
                      source="payment_expiration_seconds"
                      label={translate('ara_tiquets_admin.payment_expiration_seconds')}
                      validate={[required(), minValue(1)]}
                      options={{ min: 1, step: 1 }}
                      fullWidth
                    />
                    <NumberInput
                      source="payment_success_limit_seconds"
                      label={translate('ara_tiquets_admin.payment_success_limit_seconds')}
                      validate={[required(), minValue(1)]}
                      options={{ min: 1, step: 1 }}
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff', mt: 3 }}>
                <Typography variant="h6" sx={{ px: 2.5, pt: 2.5, fontWeight: 600 }}>
                  {translate('ara_tiquets_admin.reminders')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ px: 2.5, pt: 1 }}>
                  {translate('ara_tiquets_admin.reminders_help')}
                </Typography>
                <Box sx={{ p: 2.5 }}>
                  <BooleanInput
                    source="reminders_enabled"
                    label={translate('ara_tiquets_admin.reminders_enabled')}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
                    <NumberInput
                      source="reminder_hours_before"
                      label={translate('ara_tiquets_admin.reminder_hours_before')}
                      validate={[required(), minValue(1), maxValue(720)]}
                      options={{ min: 1, max: 720, step: 1 }}
                      fullWidth
                    />
                    <TextInput
                      source="reminder_send_from"
                      label={translate('ara_tiquets_admin.reminder_send_from')}
                      type="time"
                      validate={required()}
                      fullWidth
                    />
                    <TextInput
                      source="reminder_send_until"
                      label={translate('ara_tiquets_admin.reminder_send_until')}
                      type="time"
                      validate={required()}
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff', mt: 3 }}>
                <Typography variant="h6" sx={{ px: 2.5, pt: 2.5, fontWeight: 600 }}>
                  {translate('ara_tiquets_admin.appearance')}
                </Typography>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5 }}>
                    <ColorInput
                      source="primary_color"
                      label={translate('ara_tiquets_admin.primary_color')}
                      validate={required()}
                      fullWidth
                    />
                    <ColorInput
                      source="secondary_color"
                      label={translate('ara_tiquets_admin.secondary_color')}
                      validate={required()}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 3, mt: 2.5 }}>
                    <LogoConfiguration title={translate('ara_tiquets_admin.web_logo')} target={targets.ARA_TIQUETS_WEB_LOGO} />
                    <LogoConfiguration title={translate('ara_tiquets_admin.web_icon')} target={targets.ARA_TIQUETS_WEB_ICON} />
                    <LogoConfiguration title={translate('ara_tiquets_admin.email_logo')} target={targets.ARA_TIQUETS_EMAIL_LOGO} />
                    <LogoConfiguration title={translate('ara_tiquets_admin.ticket_pdf_logo')} target={targets.ARA_TIQUETS_TICKET_PDF_LOGO} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 3, mt: 3 }}>
                    <LogoConfiguration title={translate('ara_tiquets_admin.default_event_web_image')} target={targets.ARA_TIQUETS_DEFAULT_EVENT_WEB_IMAGE} />
                    <LogoConfiguration title={translate('ara_tiquets_admin.default_event_ticket_pdf_image')} target={targets.ARA_TIQUETS_DEFAULT_EVENT_TICKET_PDF_IMAGE} />
                    <LogoConfiguration title={translate('ara_tiquets_admin.default_subscription_image')} target={targets.ARA_TIQUETS_DEFAULT_SUBSCRIPTION_IMAGE} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
                    <SaveButton
                      disabled={saving}
                      saving={saving}
                      handleSubmitWithRedirect={handleSubmitWithRedirect}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        />
      ) : null}
    </Box>
  );
};

const LegalTextsConfiguration = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [activeLanguage, setActiveLanguage] = useState(languageIds[0]);
  const [translations, setTranslations] = useState(() => normalizeTranslations([]));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetch(`${apiUrl}/ara-tiquets/configuration`, { headers: headersAPIObjectAuth() })
      .then(response => {
        if (!response.ok) throw new Error('configuration_load_error');
        return response.json();
      })
      .then(data => {
        if (mounted) setTranslations(normalizeTranslations(data.translations));
      })
      .catch(() => notify(translate('ara_tiquets_admin.configuration_load_error'), 'error'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [notify, translate]);

  const updateField = (field, value) => {
    setTranslations(current => current.map(item => (
      item.lang === activeLanguage ? { ...item, [field]: value } : item
    )));
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/ara-tiquets/configuration`, {
        method: 'PUT',
        headers: headersAPIObjectAuth(),
        body: JSON.stringify({ translations })
      });
      if (!response.ok) throw new Error('configuration_save_error');
      const data = await response.json();
      setTranslations(normalizeTranslations(data.translations));
      notify(translate('ara_tiquets_admin.configuration_saved'), 'info');
    } catch (error) {
      notify(translate('ara_tiquets_admin.configuration_save_error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const current = translations.find(item => item.lang === activeLanguage) || emptyTranslation(activeLanguage);

  return (
    <Box sx={{ mt: 3, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff' }}>
      <Typography variant="h6" sx={{ px: 2.5, pt: 2.5, fontWeight: 600 }}>
        {translate('ara_tiquets_admin.legal_texts')}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ px: 2.5, pt: 1 }}>
        {translate('ara_tiquets_admin.legal_texts_project_name_help')}
      </Typography>
      <Tabs
        value={activeLanguage}
        onChange={(event, value) => setActiveLanguage(value)}
        sx={{ px: 1.5, borderBottom: '1px solid #e0e0e0' }}
      >
        {languageIds.map(lang => (
          <Tab key={lang} value={lang} label={translate(`common.language_${lang}`)} />
        ))}
      </Tabs>
      <Box sx={{ p: 2.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              {textFields.map(([field, label, rows]) => (
                <TextField
                  key={`${activeLanguage}-${field}`}
                  label={translate(`ara_tiquets_admin.${label}`)}
                  value={current[field] || ''}
                  onChange={event => updateField(field, event.target.value)}
                  multiline
                  minRows={rows}
                  fullWidth
                  variant="filled"
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
              <SaveButton
                disabled={saving}
                saving={saving}
                handleSubmitWithRedirect={save}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export const AraTiquetsConfiguration = () => {
  const translate = useTranslate();

  return (
    <>
      <Typography variant="h5" style={{ marginTop: 24, marginBottom: 16, color: '#666' }}>
        {translate('ara_tiquets_admin.configuration_title')}
      </Typography>
      <GeneralConfiguration />
      <LegalTextsConfiguration />
    </>
  );
};

export const AraTiquetsCategories = () => {
  const translate = useTranslate();

  return (
    <>
      <Typography variant="h5" style={{ marginTop: 24, marginBottom: 16, color: '#666' }}>
        {translate('main_menu.ara_tiquets_categories')}
      </Typography>
      <Typography>{translate('ara_tiquets_admin.pending')}</Typography>
    </>
  );
};

export const AraTiquetsStatistics = () => {
  const translate = useTranslate();

  return (
    <>
      <Typography variant="h5" style={{ marginTop: 24, marginBottom: 16, color: '#666' }}>
        {translate('main_menu.ara_tiquets_statistics')}
      </Typography>
      <Typography>{translate('ara_tiquets_admin.pending')}</Typography>
    </>
  );
};
